import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock3, CreditCard, ScanLine, ArrowDown } from "lucide-react";
import { WaIcon } from "./Logo";
import { IMAGES, scrollToId, waOpen, DEFAULT_MSG } from "../lib/site";
import { useContent } from "../lib/content";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: 0,
        transition: { duration: 0.95, delay: 0.15 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
    }),
};

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const ySticker = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const t = useContent().hero;

    return (
        <section
            ref={ref}
            className="relative overflow-hidden bg-gradient-to-b from-[#0E6B3F] to-[#073A22] text-[#F7F4EC]"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-10 left-0 select-none text-[19vw] font-black lowercase leading-none tracking-tighter text-outline whitespace-nowrap"
            >
                conbox conbox
            </div>

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-32 sm:pt-36 lg:pt-40 pb-20 lg:pb-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center min-h-[100svh]">
                <div>
                    <motion.span
                        data-testid="hero-badge-24h"
                        initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
                        animate={{ opacity: 1, scale: 1, rotate: -3 }}
                        transition={{ duration: 0.5, delay: 0.05 }}
                        className="inline-block bg-[#F1C75B] text-[#1A231E] text-xs sm:text-sm font-extrabold uppercase tracking-[0.14em] px-4 py-2 rounded-full sticker"
                    >
                        {t.badge}
                    </motion.span>

                    <h1 data-testid="hero-title" className="mt-6 sm:mt-8 leading-[0.98]">
                        <span className="block overflow-hidden pb-1">
                            <motion.span
                                variants={line}
                                initial="hidden"
                                animate="show"
                                custom={0}
                                className="block text-5xl sm:text-6xl lg:text-[4.6rem] font-black tracking-tight text-[#F7F4EC]"
                            >
                                {t.title1}
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-3">
                            <motion.span
                                variants={line}
                                initial="hidden"
                                animate="show"
                                custom={1}
                                className="block font-caveat text-6xl sm:text-7xl lg:text-[6rem] font-bold text-[#F1C75B] -rotate-1 origin-left"
                            >
                                {t.title2}
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55 }}
                        className="mt-6 max-w-xl text-base sm:text-lg text-[#F7F4EC]/85 leading-relaxed"
                    >
                        {t.subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.7 }}
                        className="mt-8 flex flex-wrap items-center gap-3.5"
                    >
                        <button
                            data-testid="hero-cta-simular"
                            onClick={() => scrollToId("solicitacao")}
                            className="cta-pulse inline-flex items-center gap-2 rounded-full bg-[#F57A3A] hover:bg-[#E06324] text-[#F7F4EC] font-bold px-7 py-4 text-base transition-all duration-200 hover:-translate-y-0.5"
                        >
                            {t.cta1}
                        </button>
                        <button
                            data-testid="hero-cta-whatsapp"
                            onClick={() => waOpen(DEFAULT_MSG)}
                            className="inline-flex items-center gap-2 rounded-full border-2 border-[#F7F4EC]/40 hover:border-[#F1C75B] hover:text-[#F1C75B] text-[#F7F4EC] font-bold px-7 py-4 text-base transition-all duration-200 hover:-translate-y-0.5"
                        >
                            <WaIcon className="w-5 h-5" />
                            {t.cta2}
                        </button>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-9 flex flex-wrap gap-x-6 gap-y-2.5 text-sm font-semibold text-[#F7F4EC]/80"
                    >
                        <li className="inline-flex items-center gap-2">
                            <Clock3 className="w-4.5 h-4.5 w-5 h-5 text-[#F1C75B]" /> Aberto 24h,
                            sem fila
                        </li>
                        <li className="inline-flex items-center gap-2">
                            <ScanLine className="w-5 h-5 text-[#F1C75B]" /> Acesso por QR Code ou
                            cartão
                        </li>
                        <li className="inline-flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-[#F1C75B]" /> Pix, cartão e
                            aproximação
                        </li>
                    </motion.ul>
                </div>

                <div className="relative" data-testid="hero-image-container">
                    <motion.div
                        style={{ y: yImg }}
                        className="relative rounded-[2rem] border-2 border-[#F7F4EC]/25 overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
                    >
                        <img
                            src={IMAGES.heroContainer}
                            alt="Loja contêiner Conbox na entrada de um condomínio, à noite"
                            className="w-full h-[340px] sm:h-[440px] lg:h-[540px] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#073A22]/60 via-transparent to-transparent" />
                        <div className="absolute bottom-4 right-4 rounded-xl bg-[#073A22]/80 backdrop-blur px-4 py-2.5 text-sm font-bold text-[#F7F4EC]">
                            Loja Conbox · contêiner ou espaço cedido
                        </div>
                    </motion.div>

                    <motion.div
                        style={{ y: ySticker, "--rot": "10deg" }}
                        className="absolute -top-6 -right-3 sm:-right-6 w-24 h-24 sm:w-28 sm:h-28"
                    >
                        <div className="relative w-full h-full rounded-full bg-[#F1C75B] border-2 border-[#1A231E] shadow-[5px_5px_0_#1A231E] grid place-items-center">
                            <svg viewBox="0 0 100 100" className="absolute inset-0 spin-slow">
                                <defs>
                                    <path
                                        id="circ"
                                        d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0"
                                    />
                                </defs>
                                <text fontSize="10" letterSpacing="2.5" fill="#1A231E" fontWeight="800">
                                    <textPath href="#circ">
                                        ABERTO 24H • TODO DIA • ABERTO 24H •
                                    </textPath>
                                </text>
                            </svg>
                            <Clock3 className="w-8 h-8 text-[#0E6B3F]" />
                        </div>
                    </motion.div>

                    <div
                        style={{ "--rot": "-6deg" }}
                        className="float-soft absolute -bottom-5 -left-3 sm:-left-6 rounded-2xl bg-[#F57A3A] text-[#F7F4EC] font-extrabold text-sm px-4 py-3 sticker"
                    >
                        Sem filas. Sem espera.
                    </div>
                </div>
            </div>

            <button
                onClick={() => scrollToId("beneficios")}
                aria-label="Rolar para benefícios"
                className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[#F7F4EC]/70 hover:text-[#F1C75B] transition-colors hidden lg:block"
            >
                <ArrowDown className="w-6 h-6 animate-bounce" />
            </button>
        </section>
    );
}
